import { Panel, PanelBody, PanelHeader, PanelSpacer, PanelTitle } from '../../../components/ui/Panel';
import { Stat, StatList } from '../../../components/ui/Stat';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';

/**
 * Live counters for the running campaign.
 *
 * Two panels rather than one, colour-coded green and blue, because the old dashboard
 * kept them separate and the distinction is real: a test send is a handful of
 * placement checks with no notion of total or pending, while a bulk campaign has six
 * meaningful counters. One panel trying to serve both showed four dashes half the time.
 *
 * Every number here comes from a single /status response. That matters: the old client
 * computed `pending` itself and also wrote an optimistic value at submit — subtracting
 * *enqueued* work rather than *settled* work — which made Pending the one counter that
 * moved instantly and then visibly jumped back up on the next poll. One definition, one
 * source.
 */

export function TestLiveStatus({ status, onRefresh, isRefreshing }) {
  return (
    <Panel variant="test">
      <PanelHeader>
        <PanelTitle icon="fa-flask" variant="test">
          Test Mode Live Status
        </PanelTitle>
      </PanelHeader>

      <PanelBody>
        <StatList columns={2}>
          <Stat icon="fa-paper-plane" label="Total Sent" value={status?.sent ?? 0} tone="success" live surface="success" />
          <Stat
            icon="fa-circle-exclamation"
            label="Total Failed"
            value={status?.failed ?? 0}
            tone={status?.failed ? 'danger' : 'default'}
            live
            surface="success"
          />
        </StatList>

        <Button
          variant="success"
          size="sm"
          icon="fa-sync-alt"
          onClick={onRefresh}
          loading={isRefreshing}
          loadingLabel="Refreshing…"
          className="self-start"
        >
          Refresh current status
        </Button>
      </PanelBody>
    </Panel>
  );
}

export function BulkLiveStatus({
  status,
  batchLimit,
  isStopped,
  campaignStatus,
  onRetryFailed,
  isRetryingFailed
}) {
  const processing = status?.processing ?? status?.sending ?? 0;
  const uncertain = status?.uncertain ?? 0;
  const cancelled = status?.cancelled ?? 0;
  // Offered only while nothing is being processed, so retrying is a deliberate step
  // between runs rather than something that changes a run under the operator's feet.
  const canRetryFailed = Boolean(onRetryFailed) && (status?.failed ?? 0) > 0 && processing === 0;

  return (
    <Panel variant="bulk">
      <PanelHeader>
        <PanelTitle icon="fa-chart-line" variant="bulk">
          Bulk Mode Live Status
        </PanelTitle>
        <PanelSpacer />
        {isStopped ? (
          <Badge tone="stopped">Stopped</Badge>
        ) : campaignStatus ? (
          <Badge tone={campaignStatus === 'in_progress' ? 'in_progress' : campaignStatus}>
            {campaignStatus === 'in_progress' ? 'In progress' : campaignStatus}
          </Badge>
        ) : null}
      </PanelHeader>

      <PanelBody>
        <StatList columns={2}>
          <Stat icon="fa-list" label="Total" value={status?.total ?? 0} surface="brand" />
          {/*
            Every recipient still owed an email. Falls only as recipients are processed or
            cancelled — a stop pauses the campaign and leaves this exactly where it was.
          */}
          <Stat
            icon="fa-clock"
            label="Queue"
            value={status?.queue ?? status?.pending ?? 0}
            tone="info"
            live
            surface="brand"
          />
          {/* What was typed into Limit for the batch in flight — a local, known value. */}
          <Stat icon="fa-swatchbook" label="Limit" value={batchLimit ?? 0} surface="brand" />
          {/* The part of the queue the workers hold right now; 0 while paused. */}
          <Stat
            icon="fa-hourglass-half"
            label="Processing"
            value={processing}
            tone="warning"
            live
            surface="brand"
          />
          <Stat icon="fa-paper-plane" label="Total Sent" value={status?.sent ?? 0} tone="success" live surface="brand" />
          <Stat
            icon="fa-circle-exclamation"
            label="Total Failed"
            value={status?.failed ?? 0}
            tone={status?.failed ? 'danger' : 'default'}
            live
            surface="brand"
          />
        </StatList>

        {/*
          The two ways a recipient leaves the queue without being sent or failed. Named so
          they are never an unexplained gap between Total and the other counters.
        */}
        {uncertain > 0 ? (
          <p className="text-xs text-muted">
            <i className="fa-solid fa-circle-question mr-1.5" aria-hidden="true" />
            {uncertain.toLocaleString()} recipient{uncertain === 1 ? ' was' : 's were'} being sent when a
            worker stopped responding. They may already have been delivered, so they are not retried
            automatically.
          </p>
        ) : null}
        {cancelled > 0 ? (
          <p className="text-xs text-muted">
            <i className="fa-solid fa-ban mr-1.5" aria-hidden="true" />
            {cancelled.toLocaleString()} recipient{cancelled === 1 ? '' : 's'} cancelled.
          </p>
        ) : null}

        {canRetryFailed ? (
          <Button
            variant="secondary"
            size="sm"
            icon="fa-rotate-right"
            onClick={onRetryFailed}
            loading={isRetryingFailed}
            loadingLabel="Returning to queue…"
            className="self-start"
          >
            Retry failed recipients
          </Button>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

/**
 * The persistent campaign status line — the old `#errors` element.
 *
 * Kept as a permanent line rather than folded into the toast system, because it is
 * state rather than an event: "Campaign is running — 412 sent" must stay on screen. It
 * is a live region here, which it was not before, so the counters are announced as they
 * change.
 */
export function ResultsLine({ message, onDownloadLog, isDownloadingLog, canDownloadLog }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-surface px-3 py-2.5">
      <p
        className="flex items-start gap-2 text-base text-ink-700"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <i className="fa-solid fa-circle-info mt-0.5 shrink-0 text-ink-400" aria-hidden="true" />
        <span className="min-w-0 flex-1 break-words">
          {message || 'Results will appear here once a campaign is submitted.'}
        </span>
      </p>

      {/*
        Offered whenever the campaign has recorded a failure, because that is exactly when the
        status line says "download the log for details" — a message the previous UI showed
        while having no way to act on it, since its log-download button had been removed from
        the markup.
      */}
      {canDownloadLog ? (
        <Button
          variant="secondary"
          size="sm"
          icon="fa-download"
          onClick={onDownloadLog}
          loading={isDownloadingLog}
          loadingLabel="Preparing…"
          className="self-start"
        >
          Download per-recipient log
        </Button>
      ) : null}
    </div>
  );
}
